import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getItems, createItem, updateItem, deleteItem, searchCarriers,
  FIG_LISTS,
} from '../../api/figApi';
import {
  POLICY_LOB_OPTIONS, POLICY_STATUS_OPTIONS, INVOICE_FEES_OPTIONS, emptyPolicy,
} from './policyFormOptions';
import { CockpitModal, CockpitModalButton } from './CockpitModal';

/**
 * CockpitPolicyEditor — cockpit-styled inline editor for a single FIG_Policies
 * record, invoked from the Dashboard's policy detail view.
 *
 * Mirrors the Clients-tab form logic (FIGInsured.js renderPolicyForm) without
 * sharing state. Uses the same SharePoint lists + API helpers.
 *
 * Drawers (one open at a time):
 *   POLICY INFO | FINANCIAL | ADDITIONAL FEES | LAYER PARTICIPANTS | ENDORSEMENTS
 *
 * Props:
 *   policy           — FIG_Policies record being edited (must include .id)
 *   insured          — FIG_Insureds record (used to stamp InsuredId on new sub-rows)
 *   getToken         — async token resolver
 *   onPolicyUpdated  — (policyId, optimisticPatch) after successful save
 *   onPolicyDeleted  — () after successful delete
 *   onClose          — request to close the editor (not currently invoked internally;
 *                      parent can use it to render its own close affordance)
 */
export default function CockpitPolicyEditor({
  policy, insured, getToken,
  onPolicyUpdated, onPolicyDeleted, onClose, // eslint-disable-line no-unused-vars
}) {
  // -------- Form field state (Policy Info + Financial fields live here) --------
  const [fields, setFields] = useState(() => ({ ...emptyPolicy, ...(policy || {}) }));
  useEffect(() => {
    setFields({ ...emptyPolicy, ...(policy || {}) });
  }, [policy?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------- Drawer control (only one open at a time) --------
  // null | 'policyInfo' | 'financial' | 'additionalFees' | 'participants' | 'endorsements'
  const [openDrawer, setOpenDrawer] = useState(null);

  // -------- Save flag (shared across all save paths) --------
  const [saving, setSaving] = useState(false);

  // -------- List-backed sections --------
  const [policyFees, setPolicyFees] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [endorsements, setEndorsements] = useState([]);

  // -------- Carrier typeahead --------
  const [carrierResults, setCarrierResults] = useState([]);
  const [carrierSearching, setCarrierSearching] = useState(false);
  const [showCarrierResults, setShowCarrierResults] = useState(false);
  const carrierTimerRef = useRef(null);
  useEffect(() => () => { if (carrierTimerRef.current) clearTimeout(carrierTimerRef.current); }, []);

  // -------- Loss run sources (dropdown behind LossRunSourceId) --------
  // FIG_LossRunSources is the shared "who to ask" directory; the policy stores
  // the chosen row's item id as text. Blank = unassigned.
  const [lossRunSources, setLossRunSources] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = await getToken();
        const rows = await getItems(token, FIG_LISTS.LOSS_RUN_SOURCES);
        if (alive) setLossRunSources([...rows].sort((a, b) => String(a.Title || '').localeCompare(String(b.Title || ''))));
      } catch (err) {
        console.warn('[CockpitPolicyEditor] loss run sources unavailable:', err.message);
      }
    })();
    return () => { alive = false; };
  }, [getToken]);

  // -------- Destructive-action modals --------
  // null | { kind: 'cancel', date, reason }
  //      | { kind: 'terminate' }
  //      | { kind: 'delete' }
  //      | { kind: 'delete-participant', id, name }
  //      | { kind: 'delete-endorsement', id, number }
  //      | { kind: 'delete-fee', id, label }
  const [modal, setModal] = useState(null);

  // -------- Load sub-lists when policy changes --------
  const loadSubs = useCallback(async () => {
    if (!policy?.id) return;
    try {
      const token = await getToken();
      const [pItems, eItems, fItems] = await Promise.all([
        getItems(token, FIG_LISTS.LAYER_PARTICIPANTS),
        getItems(token, FIG_LISTS.ENDORSEMENTS),
        getItems(token, FIG_LISTS.POLICY_FEES),
      ]);
      const pid = String(policy.id);
      setParticipants(pItems.filter(p => String(p.PolicyId) === pid));
      setEndorsements(eItems.filter(e => String(e.PolicyId) === pid));
      setPolicyFees(fItems.filter(f => String(f.PolicyId) === pid));
    } catch (err) {
      console.error('[CockpitPolicyEditor] loadSubs error:', err?.message);
    }
  }, [policy?.id, getToken]);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  // -------- Field helpers --------
  const f = (key) => fields[key] ?? '';
  const set = (key, val) => setFields(prev => ({ ...prev, [key]: val }));

  // -------- Carrier typeahead handlers --------
  function handleCarrierSearch(text) {
    if (carrierTimerRef.current) clearTimeout(carrierTimerRef.current);
    set('Carrier', text);
    if (text.length < 2) {
      setCarrierResults([]);
      setShowCarrierResults(false);
      return;
    }
    carrierTimerRef.current = setTimeout(async () => {
      setCarrierSearching(true);
      try {
        const token = await getToken();
        const results = await searchCarriers(token, text);
        setCarrierResults(results);
        setShowCarrierResults(results.length > 0);
      } catch (err) {
        console.error('[CockpitPolicyEditor] carrier search failed:', err?.message);
      } finally {
        setCarrierSearching(false);
      }
    }, 300);
  }
  function selectCarrier(c) {
    setFields(prev => ({
      ...prev,
      Carrier: c.CompanyName || '',
      NAICCode: c.NAICCode || '',
      AdmittedStatus: c.CarrierType || '',
    }));
    setShowCarrierResults(false);
  }

  // -------- Primary save — Policy Info + Financial (single row on FIG_POLICIES) --------
  async function handleSave() {
    if (!policy?.id) return;
    setSaving(true);
    try {
      const token = await getToken();
      await updateItem(token, FIG_LISTS.POLICIES, policy.id, fields);
      // Optimistic patch handed to parent so UI reflects new Status / Premium etc.
      // before the refetch reconciles.
      onPolicyUpdated && onPolicyUpdated(policy.id, { ...fields });
    } catch (err) {
      alert('Error saving policy: ' + (err?.message || err));
    } finally {
      setSaving(false);
    }
  }
  function handleSaveCancel() {
    // Resets edited fields back to current props.policy snapshot and closes drawer.
    setFields({ ...emptyPolicy, ...(policy || {}) });
    setOpenDrawer(null);
  }

  // -------- Additional Fees --------
  async function savePolicyFee(row, existingId) {
    if (!policy?.id) return;
    const token = await getToken();
    if (existingId) {
      await updateItem(token, FIG_LISTS.POLICY_FEES, existingId, row);
    } else {
      await createItem(token, FIG_LISTS.POLICY_FEES, {
        Title: row.FeeLabel || 'Fee',
        PolicyId: Number(policy.id),
        ...row,
      });
    }
    await loadSubs();
  }
  async function deletePolicyFee(id) {
    const token = await getToken();
    await deleteItem(token, FIG_LISTS.POLICY_FEES, id);
    await loadSubs();
  }

  // -------- Layer Participants --------
  async function saveParticipant(row, existingId) {
    if (!policy?.id) return;
    const token = await getToken();
    if (existingId) {
      await updateItem(token, FIG_LISTS.LAYER_PARTICIPANTS, existingId, row);
    } else {
      await createItem(token, FIG_LISTS.LAYER_PARTICIPANTS, {
        Title: row.ParticipantName || 'Participant',
        PolicyId: String(policy.id),
        InsuredId: insured?.id ? String(insured.id) : '',
        ...row,
      });
    }
    await loadSubs();
  }
  async function deleteParticipant(id) {
    const token = await getToken();
    await deleteItem(token, FIG_LISTS.LAYER_PARTICIPANTS, id);
    await loadSubs();
  }

  // -------- Endorsements --------
  async function saveEndorsement(row, existingId) {
    if (!policy?.id) return;
    const token = await getToken();
    if (existingId) {
      await updateItem(token, FIG_LISTS.ENDORSEMENTS, existingId, row);
    } else {
      await createItem(token, FIG_LISTS.ENDORSEMENTS, {
        Title: row.EndorsementNumber || 'Endorsement',
        PolicyId: String(policy.id),
        InsuredId: insured?.id ? String(insured.id) : '',
        ...row,
      });
    }
    await loadSubs();
  }
  async function deleteEndorsement(id) {
    const token = await getToken();
    await deleteItem(token, FIG_LISTS.ENDORSEMENTS, id);
    await loadSubs();
  }

  // -------- Destructive actions (invoked via modals) --------
  async function doCancelPolicy() {
    if (!policy?.id || !modal?.date) return;
    setSaving(true);
    try {
      const token = await getToken();
      const patch = {
        ...fields,
        Status: 'Cancelled',
        CancellationEffectiveDate: modal.date,
        CancellationReason: modal.reason || '',
      };
      await updateItem(token, FIG_LISTS.POLICIES, policy.id, patch);
      setModal(null);
      onPolicyUpdated && onPolicyUpdated(policy.id, patch);
    } catch (err) {
      alert('Error cancelling policy: ' + (err?.message || err));
    } finally {
      setSaving(false);
    }
  }
  async function doTerminatePolicy() {
    if (!policy?.id) return;
    setSaving(true);
    try {
      const token = await getToken();
      const patch = { ...fields, Status: 'Terminated' };
      await updateItem(token, FIG_LISTS.POLICIES, policy.id, patch);
      setModal(null);
      onPolicyUpdated && onPolicyUpdated(policy.id, patch);
    } catch (err) {
      alert('Error terminating policy: ' + (err?.message || err));
    } finally {
      setSaving(false);
    }
  }
  async function doDeletePolicy() {
    if (!policy?.id) return;
    setSaving(true);
    try {
      const token = await getToken();
      await deleteItem(token, FIG_LISTS.POLICIES, policy.id);
      setModal(null);
      onPolicyDeleted && onPolicyDeleted();
    } catch (err) {
      alert('Error deleting policy: ' + (err?.message || err));
    } finally {
      setSaving(false);
    }
  }

  // -------- Derived values --------
  const currentStatus = (f('Status') || '').toLowerCase();
  const canCancel = currentStatus === 'active' || currentStatus === 'bound';
  const canTerminate = currentStatus === 'cancelled';
  const showCancellationFields = currentStatus === 'cancelled' || currentStatus === 'terminated';

  const currentPremiumLive = useMemo(() => {
    const base = Number(fields.Premium) || 0;
    const endorsTotal = endorsements.reduce((s, e) => s + (Number(e.PremiumChange) || 0), 0);
    return base + endorsTotal;
  }, [fields.Premium, endorsements]);

  function toggleDrawer(key) {
    setOpenDrawer(prev => prev === key ? null : key);
  }

  // -------- Drawer-button helper --------
  function DrawerBtn({ k, children, badge }) {
    const active = openDrawer === k;
    return (
      <button
        type="button"
        className={'FIG-cpe-drawer-btn' + (active ? ' FIG-cpe-drawer-btn--active' : '')}
        onClick={() => toggleDrawer(k)}
        aria-pressed={active}
      >
        <span>{children}</span>
        {badge != null && <span className="FIG-cpe-drawer-btn-badge">{badge}</span>}
      </button>
    );
  }

  // --- Shared footer for Policy Info + Financial drawers ---
  const saveFooter = (
    <div className="FIG-cpe-save-actions">
      <button type="button" className="FIG-cpe-update-btn"
        onClick={handleSave} disabled={saving}>
        {saving ? 'SAVING…' : 'UPDATE POLICY'}
      </button>
      <button type="button" className="FIG-cpe-cancel-btn"
        onClick={handleSaveCancel} disabled={saving}>
        CANCEL
      </button>
    </div>
  );

  return (
    <div className="FIG-cpe-root">
      {/* ---- DRAWER BUTTON ROW ---- */}
      <div className="FIG-cpe-drawer-row">
        <DrawerBtn k="policyInfo">POLICY INFO</DrawerBtn>
        <DrawerBtn k="financial">FINANCIAL</DrawerBtn>
        <DrawerBtn k="additionalFees" badge={policyFees.length || null}>ADDITIONAL FEES</DrawerBtn>
        <DrawerBtn k="participants" badge={participants.length || null}>LAYER PARTICIPANTS</DrawerBtn>
        <DrawerBtn k="endorsements" badge={endorsements.length || null}>ENDORSEMENTS</DrawerBtn>
      </div>

      {/* ---- POLICY INFO drawer ---- */}
      {openDrawer === 'policyInfo' && (
        <div className="FIG-cpe-drawer">
          <div className="FIG-cpe-drawer-header">POLICY INFO</div>
          <div className="FIG-cpe-drawer-body">
            <div className="FIG-cpe-field-grid">
              <label className="FIG-cpe-field">
                <span className="FIG-cpe-field-label">LINE OF BUSINESS</span>
                <select className="FIG-cpe-input" value={f('LOB')} onChange={e => set('LOB', e.target.value)}>
                  {POLICY_LOB_OPTIONS.map(o => <option key={o} value={o}>{o || '\u2014'}</option>)}
                </select>
              </label>
              <label className="FIG-cpe-field">
                <span className="FIG-cpe-field-label">STATUS</span>
                <select className="FIG-cpe-input" value={f('Status')} onChange={e => set('Status', e.target.value)}>
                  {POLICY_STATUS_OPTIONS.map(o => <option key={o} value={o}>{o || '\u2014'}</option>)}
                </select>
              </label>
              <div className="FIG-cpe-field FIG-cpe-field--carrier">
                <span className="FIG-cpe-field-label">CARRIER</span>
                <input className="FIG-cpe-input" value={f('Carrier')}
                  onChange={e => handleCarrierSearch(e.target.value)}
                  onFocus={() => { if (carrierResults.length > 0) setShowCarrierResults(true); }}
                  onBlur={() => setTimeout(() => setShowCarrierResults(false), 200)}
                  placeholder="Type to search carriers..."
                  autoComplete="off" />
                {carrierSearching && <span className="FIG-cpe-carrier-spinner">…</span>}
                {showCarrierResults && carrierResults.length > 0 && (
                  <div className="FIG-cpe-carrier-dropdown">
                    {carrierResults.map(c => (
                      <div key={c.id} className="FIG-cpe-carrier-item"
                        onMouseDown={() => selectCarrier(c)}>
                        <div className="FIG-cpe-carrier-name">
                          {c.CompanyName}
                          {c.IsMyCarrier && <span className="FIG-cpe-carrier-badge">MY CARRIER</span>}
                        </div>
                        <div className="FIG-cpe-carrier-meta">
                          NAIC: {c.NAICCode || '—'} {c.CarrierType ? ' · ' + c.CarrierType : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">NAIC CODE</span>
                <input className="FIG-cpe-input" value={f('NAICCode')} onChange={e => set('NAICCode', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">POLICY NUMBER</span>
                <input className="FIG-cpe-input" value={f('PolicyNumber')} onChange={e => set('PolicyNumber', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">REFERENCE NUMBER</span>
                <input className="FIG-cpe-input" value={f('ReferenceNumber')} onChange={e => set('ReferenceNumber', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">QUOTE NUMBER</span>
                <input className="FIG-cpe-input" value={f('QuoteNumber')} onChange={e => set('QuoteNumber', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">EFFECTIVE DATE</span>
                <input className="FIG-cpe-input" value={f('EffectiveDate')} onChange={e => set('EffectiveDate', e.target.value)} placeholder="MM/DD/YYYY" /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">EXPIRATION DATE</span>
                <input className="FIG-cpe-input" value={f('ExpirationDate')} onChange={e => set('ExpirationDate', e.target.value)} placeholder="MM/DD/YYYY" /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">LAYER</span>
                <input className="FIG-cpe-input" value={f('Layer')} onChange={e => set('Layer', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">ADMITTED STATUS</span>
                <input className="FIG-cpe-input" value={f('AdmittedStatus')} onChange={e => set('AdmittedStatus', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">BROKER CONTACT</span>
                <input className="FIG-cpe-input" value={f('BrokerContact')} onChange={e => set('BrokerContact', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">AGENCY OF RECORD</span>
                <input className="FIG-cpe-input" value={f('AgencyOfRecord')} onChange={e => set('AgencyOfRecord', e.target.value)}
                  placeholder="Retail agency that wrote this term" title="The retail agency of record for this term — not the wholesaler" /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">LOSS RUN SOURCE</span>
                <select className="FIG-cpe-input" value={f('LossRunSourceId')} onChange={e => set('LossRunSourceId', e.target.value)}>
                  <option value="">{'— unassigned —'}</option>
                  {f('LossRunSourceId') !== '' && !lossRunSources.some(s => String(s.id) === String(f('LossRunSourceId'))) && (
                    <option value={f('LossRunSourceId')}>Source #{f('LossRunSourceId')} (not in directory)</option>
                  )}
                  {lossRunSources.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.Title}{s.Kind ? ` (${s.Kind})` : ''}</option>
                  ))}
                </select></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">PROGRAM NAME</span>
                <input className="FIG-cpe-input" value={f('ProgramName')} onChange={e => set('ProgramName', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">PROGRAM CODE</span>
                <input className="FIG-cpe-input" value={f('ProgramCode')} onChange={e => set('ProgramCode', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">UNDERWRITER</span>
                <input className="FIG-cpe-input" value={f('Underwriter')} onChange={e => set('Underwriter', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">UNDERWRITER OFFICE</span>
                <input className="FIG-cpe-input" value={f('UnderwriterOffice')} onChange={e => set('UnderwriterOffice', e.target.value)} /></label>
              <label className="FIG-cpe-field FIG-cpe-field--checkbox">
                <input className="FIG-cpe-checkbox" type="checkbox"
                  checked={fields.AgencyBilled === true}
                  onChange={e => set('AgencyBilled', e.target.checked)} />
                <span className="FIG-cpe-field-label">AGENCY BILLED</span>
              </label>
              {showCancellationFields && (
                <>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">CANCELLATION EFFECTIVE</span>
                    <input className="FIG-cpe-input" value={f('CancellationEffectiveDate')}
                      onChange={e => set('CancellationEffectiveDate', e.target.value)} placeholder="MM/DD/YYYY" /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">CANCELLATION REASON</span>
                    <input className="FIG-cpe-input" value={f('CancellationReason')}
                      onChange={e => set('CancellationReason', e.target.value)} /></label>
                </>
              )}
            </div>
          </div>
          {saveFooter}
        </div>
      )}

      {/* ---- FINANCIAL drawer ---- */}
      {openDrawer === 'financial' && (
        <div className="FIG-cpe-drawer">
          <div className="FIG-cpe-drawer-header">FINANCIAL</div>
          <div className="FIG-cpe-drawer-body">
            <div className="FIG-cpe-field-grid">
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ PREMIUM</span>
                <input className="FIG-cpe-input" type="number" value={f('Premium')} onChange={e => set('Premium', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ STATE TAX</span>
                <input className="FIG-cpe-input" type="number" value={f('StateTax')} onChange={e => set('StateTax', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ STAMPING FEE</span>
                <input className="FIG-cpe-input" type="number" value={f('StampingFee')} onChange={e => set('StampingFee', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ INSPECTION FEE</span>
                <input className="FIG-cpe-input" type="number" value={f('InspectionFee')} onChange={e => set('InspectionFee', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ BROKER FEE</span>
                <input className="FIG-cpe-input" type="number" value={f('BrokerFee')} onChange={e => set('BrokerFee', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ POLICY FEE</span>
                <input className="FIG-cpe-input" type="number" value={f('PolicyFee')} onChange={e => set('PolicyFee', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ AGENCY FEE</span>
                <input className="FIG-cpe-input" type="number" value={f('AgencyFee')} onChange={e => set('AgencyFee', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">AGENCY FEE %</span>
                <input className="FIG-cpe-input" type="number" value={f('AgencyFeePercent')} onChange={e => set('AgencyFeePercent', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">COMMISSION %</span>
                <input className="FIG-cpe-input" type="number" value={f('CommissionPercent')} onChange={e => set('CommissionPercent', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ COMMISSION AMOUNT</span>
                <input className="FIG-cpe-input" type="number" value={f('CommissionAmount')} onChange={e => set('CommissionAmount', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">WHOLESALE BROKER</span>
                <input className="FIG-cpe-input" value={f('WholesaleBroker')} onChange={e => set('WholesaleBroker', e.target.value)} /></label>
              <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">INVOICE FEES DISPLAY</span>
                <select className="FIG-cpe-input" value={f('InvoiceFeesDisplay')} onChange={e => set('InvoiceFeesDisplay', e.target.value)}>
                  {INVOICE_FEES_OPTIONS.map(o => <option key={o} value={o}>{o || '\u2014'}</option>)}
                </select>
              </label>
            </div>
          </div>
          {saveFooter}
        </div>
      )}

      {/* ---- ADDITIONAL FEES drawer ---- */}
      {openDrawer === 'additionalFees' && (
        <div className="FIG-cpe-drawer">
          <div className="FIG-cpe-drawer-header">ADDITIONAL FEES</div>
          <div className="FIG-cpe-drawer-body">
            {policyFees.length === 0 && <div className="FIG-cpe-empty">NO ADDITIONAL FEES</div>}
            {policyFees.map(fee => (
              <div key={fee.id} className="FIG-cpe-list-row">
                <div className="FIG-cpe-field-grid">
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">LABEL</span>
                    <input className="FIG-cpe-input" defaultValue={fee.FeeLabel || ''}
                      onBlur={e => savePolicyFee({ ...fee, FeeLabel: e.target.value }, fee.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ AMOUNT</span>
                    <input className="FIG-cpe-input" type="number" defaultValue={fee.Amount ?? ''}
                      onBlur={e => savePolicyFee({ ...fee, Amount: e.target.value }, fee.id)} /></label>
                  <label className="FIG-cpe-field FIG-cpe-field--wide"><span className="FIG-cpe-field-label">NOTES</span>
                    <input className="FIG-cpe-input" defaultValue={fee.Notes || ''}
                      onBlur={e => savePolicyFee({ ...fee, Notes: e.target.value }, fee.id)} /></label>
                </div>
                <button type="button" className="FIG-cpe-remove-btn"
                  onClick={() => setModal({ kind: 'delete-fee', id: fee.id, label: fee.FeeLabel || '(unnamed)' })}>
                  REMOVE
                </button>
              </div>
            ))}
            <button type="button" className="FIG-cpe-add-btn"
              onClick={() => savePolicyFee({ FeeLabel: '', Amount: '', Notes: '' }, null)}>
              + ADD FEE
            </button>
          </div>
        </div>
      )}

      {/* ---- LAYER PARTICIPANTS drawer ---- */}
      {openDrawer === 'participants' && (
        <div className="FIG-cpe-drawer">
          <div className="FIG-cpe-drawer-header">LAYER PARTICIPANTS</div>
          <div className="FIG-cpe-drawer-body">
            {participants.length === 0 && <div className="FIG-cpe-empty">NO PARTICIPANTS</div>}
            {participants.map(pt => (
              <div key={pt.id} className="FIG-cpe-list-row">
                <div className="FIG-cpe-field-grid">
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">NAME</span>
                    <input className="FIG-cpe-input" defaultValue={pt.ParticipantName || ''}
                      onBlur={e => saveParticipant({ ...pt, ParticipantName: e.target.value }, pt.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">TYPE</span>
                    <input className="FIG-cpe-input" defaultValue={pt.ParticipantType || ''}
                      onBlur={e => saveParticipant({ ...pt, ParticipantType: e.target.value }, pt.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">%</span>
                    <input className="FIG-cpe-input" type="number" defaultValue={pt.Percentage ?? ''}
                      onBlur={e => saveParticipant({ ...pt, Percentage: e.target.value }, pt.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">REFERENCE #</span>
                    <input className="FIG-cpe-input" defaultValue={pt.ReferenceNumber || ''}
                      onBlur={e => saveParticipant({ ...pt, ReferenceNumber: e.target.value }, pt.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">POLICY #</span>
                    <input className="FIG-cpe-input" defaultValue={pt.PolicyNumber || ''}
                      onBlur={e => saveParticipant({ ...pt, PolicyNumber: e.target.value }, pt.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">STATUS</span>
                    <input className="FIG-cpe-input" defaultValue={pt.Status || ''}
                      onBlur={e => saveParticipant({ ...pt, Status: e.target.value }, pt.id)} /></label>
                </div>
                <button type="button" className="FIG-cpe-remove-btn"
                  onClick={() => setModal({ kind: 'delete-participant', id: pt.id, name: pt.ParticipantName || '(unnamed)' })}>
                  DELETE
                </button>
              </div>
            ))}
            <button type="button" className="FIG-cpe-add-btn"
              onClick={() => saveParticipant({ ParticipantName: '', ParticipantType: '', Percentage: '', ReferenceNumber: '', PolicyNumber: '', Status: '' }, null)}>
              + ADD PARTICIPANT
            </button>
          </div>
        </div>
      )}

      {/* ---- ENDORSEMENTS drawer ---- */}
      {openDrawer === 'endorsements' && (
        <div className="FIG-cpe-drawer">
          <div className="FIG-cpe-drawer-header">
            <span>ENDORSEMENTS</span>
            <span className="FIG-cpe-drawer-header-aux">
              CURRENT PREMIUM: ${currentPremiumLive.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
          <div className="FIG-cpe-drawer-body">
            {endorsements.length === 0 && <div className="FIG-cpe-empty">NO ENDORSEMENTS</div>}
            {endorsements.map(en => (
              <div key={en.id} className="FIG-cpe-list-row">
                <div className="FIG-cpe-field-grid">
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">ENDORSEMENT #</span>
                    <input className="FIG-cpe-input" defaultValue={en.EndorsementNumber || ''}
                      onBlur={e => saveEndorsement({ ...en, EndorsementNumber: e.target.value }, en.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">EFFECTIVE DATE</span>
                    <input className="FIG-cpe-input" defaultValue={en.EffectiveDate || ''}
                      onBlur={e => saveEndorsement({ ...en, EffectiveDate: e.target.value }, en.id)}
                      placeholder="MM/DD/YYYY" /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">TYPE</span>
                    <input className="FIG-cpe-input" defaultValue={en.EndorsementType || ''}
                      onBlur={e => saveEndorsement({ ...en, EndorsementType: e.target.value }, en.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">$ PREMIUM CHANGE</span>
                    <input className="FIG-cpe-input" type="number" defaultValue={en.PremiumChange ?? ''}
                      onBlur={e => saveEndorsement({ ...en, PremiumChange: e.target.value }, en.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">STATUS</span>
                    <input className="FIG-cpe-input" defaultValue={en.Status || ''}
                      onBlur={e => saveEndorsement({ ...en, Status: e.target.value }, en.id)} /></label>
                  <label className="FIG-cpe-field FIG-cpe-field--wide"><span className="FIG-cpe-field-label">DESCRIPTION</span>
                    <input className="FIG-cpe-input" defaultValue={en.Description || ''}
                      onBlur={e => saveEndorsement({ ...en, Description: e.target.value }, en.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">REQUEST DATE</span>
                    <input className="FIG-cpe-input" defaultValue={en.RequestDate || ''}
                      onBlur={e => saveEndorsement({ ...en, RequestDate: e.target.value }, en.id)} /></label>
                  <label className="FIG-cpe-field"><span className="FIG-cpe-field-label">ISSUED DATE</span>
                    <input className="FIG-cpe-input" defaultValue={en.IssuedDate || ''}
                      onBlur={e => saveEndorsement({ ...en, IssuedDate: e.target.value }, en.id)} /></label>
                  <label className="FIG-cpe-field FIG-cpe-field--wide"><span className="FIG-cpe-field-label">NOTES</span>
                    <input className="FIG-cpe-input" defaultValue={en.Notes || ''}
                      onBlur={e => saveEndorsement({ ...en, Notes: e.target.value }, en.id)} /></label>
                </div>
                <button type="button" className="FIG-cpe-remove-btn"
                  onClick={() => setModal({ kind: 'delete-endorsement', id: en.id, number: en.EndorsementNumber || '(unnamed)' })}>
                  DELETE
                </button>
              </div>
            ))}
            <button type="button" className="FIG-cpe-add-btn"
              onClick={() => saveEndorsement({
                EndorsementNumber: '', EffectiveDate: '', Description: '', EndorsementType: '',
                PremiumChange: '', Status: '', RequestDate: '', IssuedDate: '', Notes: '',
              }, null)}>
              + ADD ENDORSEMENT
            </button>
          </div>
        </div>
      )}

      {/* ---- DESTRUCTIVE ACTIONS ---- */}
      <div className="FIG-cpe-destructive-actions">
        {canCancel && (
          <button type="button"
            className="FIG-cpe-destructive-btn FIG-cpe-destructive-btn--cancel-policy"
            onClick={() => setModal({ kind: 'cancel', date: '', reason: '' })}>
            CANCEL POLICY
          </button>
        )}
        {canTerminate && (
          <button type="button"
            className="FIG-cpe-destructive-btn FIG-cpe-destructive-btn--terminate"
            onClick={() => setModal({ kind: 'terminate' })}>
            TERMINATE POLICY
          </button>
        )}
        <button type="button"
          className="FIG-cpe-destructive-btn FIG-cpe-destructive-btn--delete"
          onClick={() => setModal({ kind: 'delete' })}>
          DELETE POLICY
        </button>
      </div>

      {/* ---- CANCEL POLICY modal ---- */}
      <CockpitModal
        open={modal?.kind === 'cancel'}
        title="CANCEL POLICY"
        onClose={() => setModal(null)}
        footer={<>
          <CockpitModalButton variant="cancel" onClick={() => setModal(null)}>BACK</CockpitModalButton>
          <CockpitModalButton variant="default" disabled={saving || !modal?.date}
            onClick={doCancelPolicy}>
            {saving ? 'SAVING…' : 'CONFIRM CANCEL'}
          </CockpitModalButton>
        </>}
      >
        <p>Provide the cancellation effective date and reason. The policy Status will change to <strong>Cancelled</strong>.</p>
        <div className="FIG-cpe-field-grid">
          <label className="FIG-cpe-field">
            <span className="FIG-cpe-field-label">EFFECTIVE DATE</span>
            <input className="FIG-cpe-input" value={modal?.date || ''}
              onChange={e => setModal(prev => ({ ...prev, date: e.target.value }))}
              placeholder="MM/DD/YYYY" autoFocus />
          </label>
          <label className="FIG-cpe-field FIG-cpe-field--wide">
            <span className="FIG-cpe-field-label">REASON</span>
            <input className="FIG-cpe-input" value={modal?.reason || ''}
              onChange={e => setModal(prev => ({ ...prev, reason: e.target.value }))} />
          </label>
        </div>
      </CockpitModal>

      {/* ---- TERMINATE POLICY modal ---- */}
      <CockpitModal
        open={modal?.kind === 'terminate'}
        title="TERMINATE POLICY"
        onClose={() => setModal(null)}
        footer={<>
          <CockpitModalButton variant="cancel" onClick={() => setModal(null)}>BACK</CockpitModalButton>
          <CockpitModalButton variant="destructive" disabled={saving}
            onClick={doTerminatePolicy}>
            {saving ? 'SAVING…' : 'CONFIRM TERMINATE'}
          </CockpitModalButton>
        </>}
      >
        <p>This policy is already <strong>Cancelled</strong>. Terminating marks the record as permanently ended. Data is preserved but the policy will not appear in active lists.</p>
      </CockpitModal>

      {/* ---- DELETE POLICY modal ---- */}
      <CockpitModal
        open={modal?.kind === 'delete'}
        title="DELETE POLICY"
        onClose={() => setModal(null)}
        footer={<>
          <CockpitModalButton variant="cancel" onClick={() => setModal(null)}>BACK</CockpitModalButton>
          <CockpitModalButton variant="destructive" disabled={saving}
            onClick={doDeletePolicy}>
            {saving ? 'DELETING…' : 'CONFIRM DELETE'}
          </CockpitModalButton>
        </>}
      >
        <p><strong>This cannot be undone.</strong></p>
        <p>Policy <strong>{fields.PolicyNumber || '(no number)'}</strong> — <strong>{fields.Carrier || '(no carrier)'}</strong> — will be permanently removed from the system along with its linked endorsements, participants, and additional fees.</p>
      </CockpitModal>

      {/* ---- DELETE PARTICIPANT modal ---- */}
      <CockpitModal
        open={modal?.kind === 'delete-participant'}
        title="DELETE PARTICIPANT"
        onClose={() => setModal(null)}
        footer={<>
          <CockpitModalButton variant="cancel" onClick={() => setModal(null)}>BACK</CockpitModalButton>
          <CockpitModalButton variant="destructive"
            onClick={async () => {
              await deleteParticipant(modal.id);
              setModal(null);
            }}>
            CONFIRM DELETE
          </CockpitModalButton>
        </>}
      >
        <p>Delete participant <strong>{modal?.name}</strong>? This cannot be undone.</p>
      </CockpitModal>

      {/* ---- DELETE ENDORSEMENT modal ---- */}
      <CockpitModal
        open={modal?.kind === 'delete-endorsement'}
        title="DELETE ENDORSEMENT"
        onClose={() => setModal(null)}
        footer={<>
          <CockpitModalButton variant="cancel" onClick={() => setModal(null)}>BACK</CockpitModalButton>
          <CockpitModalButton variant="destructive"
            onClick={async () => {
              await deleteEndorsement(modal.id);
              setModal(null);
            }}>
            CONFIRM DELETE
          </CockpitModalButton>
        </>}
      >
        <p>Delete endorsement <strong>{modal?.number}</strong>? This cannot be undone.</p>
      </CockpitModal>

      {/* ---- DELETE FEE modal ---- */}
      <CockpitModal
        open={modal?.kind === 'delete-fee'}
        title="REMOVE FEE"
        onClose={() => setModal(null)}
        footer={<>
          <CockpitModalButton variant="cancel" onClick={() => setModal(null)}>BACK</CockpitModalButton>
          <CockpitModalButton variant="destructive"
            onClick={async () => {
              await deletePolicyFee(modal.id);
              setModal(null);
            }}>
            CONFIRM REMOVE
          </CockpitModalButton>
        </>}
      >
        <p>Remove fee <strong>{modal?.label}</strong>? This cannot be undone.</p>
      </CockpitModal>
    </div>
  );
}
